import {Component, inject, Input, OnChanges, signal, WritableSignal} from '@angular/core';
import {OperationDef} from '../../core/definitions/operation-def';
import {OperationStep} from '../../core/definitions/operation-step';
import {Selector} from '../../core/definitions/reference';
import {animate, style, transition, trigger} from '@angular/animations';
import {OperationService} from '../operation.service';
import {SectionsContainer} from '../../core/definitions/sections-container';
import {OperationContainer} from '../../core/definitions/operation-container';
import {FeatureFlagsService} from "../../core/feature-flags.service";
import {MatDialog} from "@angular/material/dialog";
import {DrawerModalWrapperComponent} from "../../object-page-v2/drawer-modal-wrapper/drawer-modal-wrapper.component";
import {SearchSelectorService} from "../../core/search-selector.service";
import {SelectorContainer} from "../../core/definitions/selector-container";
import {OperationStepType} from "../../core/definitions/operation-step-type.enum";
import {CmsErrorHandlerParams, CmsErrorHandlerService} from "../../core/cms-error-handler.service";

/** A continue step whose setup finished without building a form for its object. */
export class ContinueStepWithoutFormError extends Error {
  constructor() {
    super('The continue step built no sections container');
    this.name = 'ContinueStepWithoutFormError';
  }
}

@Component({
    selector: 'app-operation-view-section-container',
    templateUrl: './operation-view-section-container.component.html',
    styleUrls: ['./operation-view-section-container.component.scss'],
    animations: [
        trigger('slideContent', [
            transition(':enter', [
                style({ overflow: 'hidden', height: '0px' }),
                animate('1000ms linear', style({ height: '*' }))
            ])
        ])
    ],
    standalone: false
})
export class OperationViewSectionContainerComponent implements OnChanges {
  // Injections
  private readonly cmsErrorHandler: CmsErrorHandlerService = inject(CmsErrorHandlerService);
  private readonly featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly modalService: MatDialog = inject(MatDialog);
  private readonly operationService: OperationService = inject(OperationService);
  private readonly searchSelectorService: SearchSelectorService = inject(SearchSelectorService);

  @Input() operationContainer: OperationContainer;
  @Input() operationStepIndex: number;
  @Input() isDialog: boolean;
  @Input() isLocationOperation: boolean;

  showSecondaryModal: WritableSignal<boolean> = signal(false);

  operation: OperationDef;
  operationStep: OperationStep;
  sectionsContainers: SectionsContainer[];

  protected readonly OperationStepType = OperationStepType;

  ngOnChanges(): void {
    console.log(this.operationContainer);
    this.setOperationStep().catch(e => this.handleStepSetupFailure(e));
  }

  async setOperationStep() {
    this.operation = this.operationContainer.currentOperation;
    this.operation.$$sectionsContainersLoadFailed = false;
    await this.operationService.setOperationStep(this.operationContainer);
    this.operationStep = this.operation.$$currentStep;
    this.sectionsContainers = this.operationService.getCurrentOperationSectionContainers(this.operationContainer);
    if (this.isContinueSubmittedObjectStep() && !this.sectionsContainers?.length) {
      throw new ContinueStepWithoutFormError();
    }
    if (this.isDialog && this.sectionsContainers) {
      this.sectionsContainers[0].isDialog = true;
      this.sectionsContainers[0].hideSectionTitle = !this.operationStep.is_copy_step;
    }
  }

  /**
   * The continue step cannot be submitted without its object, so any setup failure is flagged on the operation.
   * Only a missing form is also reported here; request failures are left to the CMS API service's own error
   * handling (see `CmsApiService.finalizeRejectedResponse`).
   */
  private handleStepSetupFailure(error: unknown) {
    console.error('Failed to set up operation step', error);
    if (this.isContinueSubmittedObjectStep()) {
      this.operation.$$sectionsContainersLoadFailed = true;
      if (error instanceof ContinueStepWithoutFormError) {
        this.cmsErrorHandler.errHandler(error, {
          clientMsg: 'TRANS__OPERATION__DISABLED_REASON__OBJECT_NOT_LOADED'
        } as CmsErrorHandlerParams);
      }
    }
  }

  private isContinueSubmittedObjectStep(): boolean {
    return this.operation?.$$currentStep?.operation_step_type === OperationStepType.CONTINUE_SUBMITTED_OBJECT;
  }

  enableSelector(selector: Selector) {
    if (this.featureFlagsService.getFeatureFlags()?.experimental?.useNewObjectPage) {
      this.showSecondaryModal.set(true);
      if (!this.operationContainer.searchContainer) {
        this.searchSelectorService.createSearchContainerForModalView(selector, <SelectorContainer>this.operationContainer).then((sc) => {
          this.searchSelectorService.setScSelectionCallback(selector, sc, {
            searchContainerCreated: () => {
            },
            selectorCallback: (selectedObj: any) => {
              this.showSecondaryModal.set(false);
              this.operationContainer.selectorEnabled = false;
              this.operationContainer.selectorCallback(selectedObj, selector);
            }
          })
          this.operationContainer.searchContainer = sc;
          this.operationContainer.selectorEnabled = true;

          this.modalService.open(DrawerModalWrapperComponent, {
            data: {
              show: this.showSecondaryModal,
              selectorContainer: this.operationContainer
            }
          });
        });
      }
      else {
        this.operationContainer.selectorEnabled = true;
        this.modalService.open(DrawerModalWrapperComponent, {
          data: {
            show: this.showSecondaryModal,
            selectorContainer: this.operationContainer
          }
        });
      }

    }
    else {
      if (this.isDialog) {
        this.operationContainer.inModal = true;
      }
      this.operationContainer.enableSelector(selector);
    }
  }
}
